import styled from '@emotion/styled';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ClassCard from 'aquilla/core/organisms/ClassCard';
import { Spacings, mediaQuery } from '@styles/index';
import { useRouter } from 'next/router';
import BottomChevron from 'aquilla/icons/actions/BottomChevron';
import Typography from 'aquilla/core/atoms/Typography';
import getParamsFromURL from '@utils/getParamsFromURL';
import CourseCarouselSkeleton from '@skeletons/CourseCard/CourseCarousel';
import useLiveClassAndCourseList from '@api/hooks/educator/useLiveClassAndCourseList';
import isEmptyObject from '@utils/isEmptyObject';
import useNotifiedList from '@api/hooks/educator/useNotifyList';
import useProfile from '@api/hooks/profile/useProfile';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { segment } from '@actions/analytics';
import { NOTIFY_ME, UN_NOTIFIED } from '@cont/NewPreSubscribed/event';
import { useSnackbar } from '@stores/snackbar';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import { notifyForUpcomingClass } from '@api/fetchers/class/updaters';
import EducatorEmptyCourse from './EducatorEmptyCourse';
import FiltersTab, { getFiltersApplied } from './FiltersTab';
import { formatClassDetail } from './utils';
import useInfiniteFetchNew from './hooks/useInfiniteFetchNew';

const Container = styled.div`
  margin-bottom: -${Spacings.SPACING_10B};
  border-bottom: 1px solid var(--color-divider);
`;
const InnerContainer = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacings.SPACING_10B} ${Spacings.SPACING_4B};
  padding: 0 0 ${Spacings.SPACING_10B} 0;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
  }

  [class*='aquilla-classcard'] {
    margin-bottom: ${Spacings.SPACING_4B};
    ${mediaQuery.uptoTablet} {
      margin-bottom: 0;
    }
  }
`;

const StyledClassCard = styled(ClassCard)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-width: unset;

    > div:first-child {
      height: unset;
      [class*='ClassThumbnailWrapper'] {
        width: 100%;

        > img {
          height: auto;
          max-width: unset;
        }
      }
    }
  }
`;
const StyledViewMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 100%;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B} ${Spacings.SPACING_2B}
    ${Spacings.SPACING_4B};
`;
const StyledViewMoreButton = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} ${Spacings.SPACING_1B}
    ${Spacings.SPACING_2B};
`;
const StyledChevronWrapper = styled.span`
  margin-top: 5px;
`;

const useClassesByFilters = (
  { userName },
  { filtersApplied },
  options = { fallbackData: undefined }
) => {
  const filterParams = {
    username: userName.toLowerCase(),
    ...filtersApplied
  };
  const key = 'sheldon/v1/list/special-class';
  const infiniteResponse = useInfiniteFetchNew(
    key,
    {
      applyOffsetsAutomatically: false,
      params: filterParams
    },
    {
      fallbackData: options.fallbackData,
      revalidateOnMount: options.revalidateOnMount
    }
  );
  return {
    key,
    ...infiniteResponse
  };
};

const Loader = () => <CourseCarouselSkeleton number={4} />;

const SpecialClasses = ({ authorID, filtersApplied }) => {
  const router = useRouter();
  const isRequestingEnroll = useRef(false);
  const { open: showSnackbar } = useSnackbar();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { profileData } = useProfile(authorID?.slice(1));
  const [activeFilters, setActiveFilters] = useState(filtersApplied.type);
  const { liveClassList } = useLiveClassAndCourseList();
  const { notifiedProgrammes, refetchNotifiedProgrammes } = useNotifiedList(
    profileData?.uid
  );
  const {
    data: filteredClassesResponse,
    setSize,
    isValidating: isFetchingFilteredClasses
  } = useClassesByFilters(
    { userName: authorID?.slice(1) },
    {
      filtersApplied: {
        ...filtersApplied,
        ...getFiltersApplied(router.query)
      }
    },
    { revalidateOnMount: false }
  );
  const [classData, setClassData] = useState(null);

  const formattedClassData = useMemo(() => {
    let classResponseData = filteredClassesResponse?.results || [];
    if (!isEmptyObject(notifiedProgrammes)) {
      classResponseData = classResponseData.map((item) => ({
        ...item,
        notified: !!notifiedProgrammes[item.uid]
      }));
    }
    if (!isEmptyObject(liveClassList)) {
      classResponseData = classResponseData.map((item) => ({
        ...item,
        isLive: !!liveClassList[item.uid]
      }));
    }
    return classResponseData;
  }, [filteredClassesResponse?.results, liveClassList, notifiedProgrammes]);

  useEffect(() => {
    const [type] = getParamsFromURL(window.location, ['type']);
    if (type !== activeFilters) setActiveFilters(type || 'latest');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    router.push(
      `/[authorID]/special-classes/?type=${activeFilters}`,
      `/${authorID}/special-classes/?type=${activeFilters}`,
      { shallow: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters]);

  const handleViewMore = () => {
    setSize((size) => size + 1);
  };

  const handleNotify = async (e, classInfo) => {
    if (e) e.stopPropagation();
    if (!isLoggedIn) {
      isRequestingEnroll.current = true;
      setClassData(classInfo);
      openLoginDrawer();
    } else {
      try {
        const { notified } = classInfo;
        const response = await notifyForUpcomingClass({
          programmeUid: classInfo.uid,
          enroll: !notified
        });
        if (!response.error) {
          segment.track(notified ? UN_NOTIFIED : NOTIFY_ME, {
            last_primary_source_section:
              'Free Live Class Batch Group Touch Point'
          });
          const message = notified
            ? 'You’ve unsubscribed class updates'
            : 'We’ll notify you when the class is live';
          showSnackbar({
            message,
            type: notified ? 'info' : 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          });
          refetchNotifiedProgrammes();
        } else {
          showSnackbar({
            message: 'Something went wrong. Please try again.',
            type: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          });
        }
      } finally {
        isRequestingEnroll.current = false;
        setClassData(null);
      }
    }
  };

  useEffect(() => {
    if (isLoggedIn && isRequestingEnroll.current && classData)
      handleNotify(null, classData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, classData]);

  const renderClasses = (classInfo) => {
    if (classInfo?.isSpecial)
      return (
        <StyledClassCard
          as="div"
          key={classInfo.uid}
          title="Special class title"
          topicColor="#8873FF"
          onNotify={(e) => handleNotify(e, classInfo)}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...formatClassDetail({
            classInfo,
            hideDetailsLine: false
          })}
        />
      );
    return null;
  };

  const handleFilterChange = (option) => {
    setActiveFilters(option);
    setSize(0);
  };

  return (
    <Container>
      <FiltersTab
        selectedFilter={activeFilters}
        filters={[
          { key: 'latest', label: 'Latest' },
          { key: 'popular', label: 'Popular' }
        ]}
        handleChange={handleFilterChange}
      />
      <InnerContainer>
        {formattedClassData?.map((freeClass) => {
          return renderClasses(freeClass);
        })}
        {!isFetchingFilteredClasses && formattedClassData?.length === 0 && (
          <EducatorEmptyCourse
            header="No classes found"
            detail="The educator doesn’t have any free classes. Select a different category or try with different filters. "
          />
        )}
        {!isFetchingFilteredClasses &&
        filteredClassesResponse?.results?.length <
          filteredClassesResponse?.count ? (
          <StyledViewMoreButtonContainer onClick={handleViewMore}>
            <StyledViewMoreButton>
              <Typography variant="h6">View more</Typography>{' '}
              <StyledChevronWrapper>
                <BottomChevron height={16} width={16} />
              </StyledChevronWrapper>
            </StyledViewMoreButton>
          </StyledViewMoreButtonContainer>
        ) : (
          <></>
        )}
        {isFetchingFilteredClasses ? <Loader /> : <></>}
      </InnerContainer>
    </Container>
  );
};

export default SpecialClasses;
